import React from "react";
import { Edit, useForm } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Form, Input, InputNumber, Select, Card, Row, Col } from "antd";
import { INVOICE_BY_ID_QUERY, UPDATE_INVOICE_MUTATION } from "./queries";
import { REQUEST_TEMPLATE_LIST_QUERY } from "../purchase-template/queries";
import { MatchStatus, InvoiceStatus } from "./interface";

const { Option } = Select;

export const InvoiceEdit: React.FC = () => {
  const { formProps, saveButtonProps } = useForm({
    meta: {
      gqlQuery: INVOICE_BY_ID_QUERY,
      gqlMutation: UPDATE_INVOICE_MUTATION,
    },
    redirect: "list",
  });

  // Purchase Templates for invoice
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates",
    filters: [
      { field: "isActive", operator: "eq", value: "true" },
      { field: "templateKind", operator: "eq", value: "INVOICE" }
    ],
    meta: { gqlQuery: REQUEST_TEMPLATE_LIST_QUERY },
  });
  const templates = Array.isArray(templatesData?.data) ? templatesData?.data : (templatesData?.data as any)?.purchaseTemplates?.nodes || [];

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Card title="Invoice Details">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Invoice Number"
                name="invoiceNumber"
                rules={[{ required: true, message: "Invoice number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Supplier Name"
                name="supplierName"
                rules={[{ required: true, message: "Supplier name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="templateId"
                label="Purchase Template"
              >
                <Select
                  placeholder="Select a template to pre-fill approval workflow"
                  loading={templatesLoading}
                  allowClear
                >
                  {(templates as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="PO Number"
                name="poNumber"
                rules={[{ required: true, message: "PO number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: "Currency is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Subtotal"
                name="subtotal"
                rules={[{ required: true, message: "Subtotal is required" }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Tax"
                name="tax"
                rules={[{ required: true, message: "Tax is required" }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Total Amount"
                name="totalAmount"
                rules={[{ required: true, message: "Total amount is required" }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Match Status"
                name="matchStatus"
              >
                <Select disabled>
                  <Option value={MatchStatus.PENDING}>{MatchStatus.PENDING}</Option>
                  <Option value={MatchStatus.MATCHED}>{MatchStatus.MATCHED}</Option>
                  <Option value={MatchStatus.MISMATCH}>{MatchStatus.MISMATCH}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Status is required" }]}
              >
                <Select>
                  <Option value={InvoiceStatus.PENDING}>{InvoiceStatus.PENDING}</Option>
                  <Option value={InvoiceStatus.MATCHED}>{InvoiceStatus.MATCHED}</Option>
                  <Option value={InvoiceStatus.APPROVED}>{InvoiceStatus.APPROVED}</Option>
                  <Option value={InvoiceStatus.PAID}>{InvoiceStatus.PAID}</Option>
                  <Option value={InvoiceStatus.REJECTED}>{InvoiceStatus.REJECTED}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Payment Request ID"
                name="paymentRequestId"
              >
                <Input disabled placeholder="Auto-generated when invoice is approved" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Delivery References"
            name="deliveryRefs"
          >
            <Select mode="tags" placeholder="Enter delivery references" />
          </Form.Item>

          <Form.Item
            label="File URL"
            name="fileId"
          >
            <Input placeholder="Invoice file URL" />
          </Form.Item>
        </Card>
      </Form>
    </Edit>
  );
};